import React, { useState, Fragment } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Chip,
  Grid,
  IconButton,
  Card,
  CardMedia,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Tooltip,
} from '@mui/material';
import { Project, ProjectCreate, Image } from '../../types';
import { useLang } from '../../contexts/langContext';
import { usePptxFiles } from '../../hooks/usePptxFiles';
import useRecommendSlideshow from '../../hooks/useRecommendSlideshow';
import { useBranding } from '../../contexts/BrandingContext';
import MaterialPicker from '../materials/MaterialPicker';
import ImagePickerContainer from '../images/ImagePickerContainer';
import ImageUpload from '../images/ImageUpload';
import { getMaterialById, uploadFileWithOutline } from '../../services/materials';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  AutoAwesome,
  ChevronRight,
  Close,
  Visibility,
  TextSnippet as TextSnippetIcon,
  TextFields as TextIcon,
  FilePresent as FileIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import MaterialStatus from '../materials/MaterialStatus';
import ProtectedImage from '../common/ProtectedImage';
import { useNavigate } from 'react-router-dom';
import OutlinePreviewDialog from '../common/OutlinePreviewDialog';
import { OutlineNode, MaterialWithFileMetadata } from '../../types';
import FileUploadDialog from '../common/FileUploadDialog';

interface ProjectFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (project: ProjectCreate) => Promise<Project>;
  initialData?: Partial<Project>;
  isEditing?: boolean;
  isSubmitting?: boolean;
  error?: string | null;
}

function ProjectFormDialog({ open, onClose, onSubmit, initialData, isEditing = false, isSubmitting = false, error: propError }: ProjectFormProps) {
  const { lang } = useLang();
  const { hints, features } = useBranding();
  const enableOutlineDocx = features?.enableOutlineDocx;
  const verbatimMode = features?.enableVerbatimMode ?? true;
  const { pptxFiles } = usePptxFiles();
  const { recommendation, isLoading: isRecommending, error: recommendationError, getRecommendation } = useRecommendSlideshow();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState<ProjectCreate>({
    title: '',
    description: '',
    numSlides: 8,
    pptxFileId: 'none',
    lang: 'zh-CN',
    ...initialData,
  });
  const [duration, setDuration] = useState<number>(15);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<MaterialWithFileMetadata[]>([]);
  const [selectedImages, setSelectedImages] = useState<Image[]>([]);

  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [outlineMaterial, setOutlineMaterial] = useState<MaterialWithFileMetadata | null>(null);
  const [outlineOption, setOutlineOption] = useState<'auto' | 'docx'>('auto');
  const [selectedHeadingLevels, setSelectedHeadingLevels] = useState<string[]>(['h1']);
  const [isDocxRecommending, setIsDocxRecommending] = useState(false);
  const [showSlideWarning, setShowSlideWarning] = useState(false);

  // Verbatim mode
  const [selectedVerbatimMaterialId, setSelectedVerbatimMaterialId] = useState<string>('');

  const handleMaterialSelect = (materials: MaterialWithFileMetadata[]) => {
    setSelectedMaterials(materials);
  };

  const handleMaterialUpload = async (file: File) => {
    const material = await uploadFileWithOutline(file);
    if (!material) return;
    setSelectedMaterials([...selectedMaterials, material]);
  };

  const handleRemoveMaterial = (materialId: string) => {
    setSelectedMaterials(selectedMaterials.filter((m) => m._id !== materialId));
    if (selectedVerbatimMaterialId === materialId) {
      setSelectedVerbatimMaterialId('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setDuration(value);
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleGetRecommendation = async () => {
    if (!formData.description?.trim()) {
      setValidationError(lang === 'en' ? 'Description is required for recommendation' : '需要描述以获取建议');
      return;
    }

    if (!duration || duration < 1) {
      setValidationError(lang === 'en' ? 'Valid presentation duration is required' : '需要有效的演示时长');
      return;
    }

    try {
      await getRecommendation({
        description: formData.description,
        durationMinutes: duration,
      });
      setActiveStep(1);
    } catch (err) {
      // Error handling is managed by the hook
    }
  };

  // Helper function to flatten nested outline structure
  const flattenOutline = (nodes: OutlineNode[]): OutlineNode[] => {
    const result: OutlineNode[] = [];

    const traverse = (nodeList: OutlineNode[]) => {
      for (const node of nodeList) {
        // Add the current node (without children to avoid circular structure)
        result.push({
          level: node.level,
          text: node.text,
          children: [],
        });

        // Recursively process children
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return result;
  };

  // Helper function to filter outline based on selected heading levels
  const filterOutlineByHeadingLevels = (nodes: OutlineNode[], levels: string[]): OutlineNode[] => {
    if (levels.length === 0) return [];

    const filtered: OutlineNode[] = [];

    const traverse = (nodeList: OutlineNode[]) => {
      for (const node of nodeList) {
        if (levels.includes(`h${node.level}`)) {
          filtered.push({
            ...node,
            children: [],
          });
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return filtered;
  };

  // Calculate slides count based on selected heading levels
  const calculateSlidesCount = (): number => {
    if (!outlineMaterial?.parsedOutline || selectedHeadingLevels.length === 0) return 0;
    return filterOutlineByHeadingLevels(outlineMaterial.parsedOutline, selectedHeadingLevels).length;
  };

  React.useEffect(() => {
    if (recommendation) {
      setFormData((prev) => ({
        ...prev,
        title: recommendation.suggestedTitle,
        description: recommendation.suggestedDescription,
        pptxFileId: recommendation.suggestedPptxFileId,
        numSlides: outlineOption === 'auto' ? recommendation.suggestedNumSlides : calculateSlidesCount() || 8,
      }));
    }
  }, [recommendation]);

  const handleImageSelect = (image: Image) => {
    if (!selectedImages.find((img) => img._id === image._id)) {
      setSelectedImages((prev) => [...prev, image]);
    }
  };

  const handleRemoveImage = (imageId: string) => {
    setSelectedImages(selectedImages.filter((img) => img._id !== imageId));
  };

  const handleDocxUpload = async (file: File) => {
    const material = await uploadFileWithOutline(file);
    setOutlineMaterial(material);
    setSelectedMaterials((prev) => [...prev, material]); // Add material to selected materials by default
  };

  const handleGetDocxRecommendation = async () => {
    if (!outlineMaterial) {
      setValidationError(lang === 'en' ? 'Outline material is not available' : '大纲材料不可用');
      return;
    }
    setValidationError(null);
    setIsDocxRecommending(true);

    try {
      // Call backend recommendation API using already uploaded material
      await getRecommendation({ fileServiceFileId: outlineMaterial.fileServiceFileId });

      // Proceed to next step
      setActiveStep(1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get DOCX recommendation:', error);
      setValidationError(lang === 'en' ? 'Failed to get recommendation' : '获取建议失败');
    } finally {
      setIsDocxRecommending(false);
    }
  };

  const handleNext = async () => {
    setValidationError(null);

    if (activeStep === 0) {
      // Move from description to recommendations
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      // Validate required fields before proceeding
      if (!formData.title?.trim()) {
        setValidationError(lang === 'en' ? 'Title is required' : '标题是必需的');
        return;
      }
      setActiveStep(2);
      return;
    }

    if (activeStep < 3) {
      // Steps 2 (materials) and 3 (images) just advance
      setActiveStep(activeStep + 1);
      return;
    }
    // Final step – create the project with all gathered info
    // Check if slides exceed 50 and show warning
    if (formData.numSlides > 50) {
      setShowSlideWarning(true);
      return;
    }

    await createProject();
  };

  const createProject = async () => {
    try {
      setLocalSubmitting(true);
      const payload: ProjectCreate = {
        ...formData,
        ...(outlineOption === 'docx' && outlineMaterial && outlineMaterial.parsedOutline
          ? { parsedOutline: filterOutlineByHeadingLevels(outlineMaterial.parsedOutline, selectedHeadingLevels) }
          : {}),
        associatedMaterialIds: selectedMaterials.map((m) => m._id),
        associatedImageIds: selectedImages.map((i) => i._id),
        ...(selectedVerbatimMaterialId ? { verbatimMaterialId: selectedVerbatimMaterialId } : {}),
      } as ProjectCreate;

      const project = await onSubmit(payload);

      // Navigate to project detail page, passing outline info for follow-up actions
      navigate(`/projects/${project._id}`, {
        state: {
          outlineOption,
          parsedOutline: outlineOption === 'docx' ? outlineMaterial?.parsedOutline : null,
          fromProjectCreation: true,
        },
      });

      onClose();
    } catch (err) {
      // parent handles error display via propError
    } finally {
      setLocalSubmitting(false);
    }
  };

  const handleOutlineMaterialSelect = (materialId: string | undefined) => {
    if (!materialId) {
      return;
    }
    // Fetch material from backend for outline
    getMaterialById(materialId).then((material) => {
      setOutlineMaterial(material);
      console.log('outlineMaterial:', material);
      // Add material to selected materials by default
      setSelectedMaterials((prev) => [...prev, material]);
    });
  };

  const handleSelectVerbatimMaterial = (materialId: string | undefined) => {
    if (!materialId) {
      return;
    }
    getMaterialById(materialId).then((material) => {
      setSelectedVerbatimMaterialId(material._id);
      // Add to selected materials if not already present
      setSelectedMaterials((prev) => {
        const exists = prev.find((m) => m._id === material._id);
        return exists ? prev : [...prev, material as MaterialWithFileMetadata];
      });
    });
  };

  const handleOutlineMaterialCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!outlineMaterial) {
      return;
    }
    const withoutOutlineMaterial = selectedMaterials.filter((material) => material._id !== outlineMaterial._id);
    if (event.target.checked) {
      setSelectedMaterials([...withoutOutlineMaterial, outlineMaterial]);
    } else {
      setSelectedMaterials(withoutOutlineMaterial);
    }
  };

  const getIcon = (type: string, size: number = 24) => {
    if (type.includes('text')) return <TextIcon sx={{ color: 'text.secondary', fontSize: size }} />;
    return <FileIcon sx={{ color: 'text.secondary', fontSize: size }} />;
  };

  const error =
    validationError ||
    propError ||
    (recommendationError && (typeof recommendationError === 'string' ? recommendationError : recommendationError.message));

  const steps = [
    lang === 'en' ? (enableOutlineDocx ? 'Project Source' : 'Start') : enableOutlineDocx ? '选择项目来源' : '开始',
    lang === 'en' ? 'Review Recommendations' : '查看建议',
    lang === 'en' ? 'Add Materials' : '添加材料',
    lang === 'en' ? 'Add Images' : '添加图片',
  ];

  const canProceed = () => {
    if (activeStep === 0) {
      if (outlineOption !== 'docx') {
        return !!formData.description?.trim() && duration >= 1;
      }
      return !!outlineMaterial; // docx path requires a file
    } else if (activeStep === 1) {
      return formData.title?.trim() && formData.pptxFileId !== 'none';
    }
    return true; // Materials and images are optional
  };

  return (
    <Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle>
          <Stack direction={['column', 'column', 'row']} spacing={4} justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{isEditing ? 'Edit Project' : lang === 'en' ? 'Create New Project' : '创建新项目'}</Typography>
            <Stepper activeStep={activeStep} sx={{ mt: [2, 2, 0] }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            minHeight: '510px',
            maxHeight: 'calc(90vh - 200px)', // Reserve space for title and actions
            overflowY: 'auto',
            flex: '1 1 auto',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} variant="filled">
              {error}
            </Alert>
          )}

          {activeStep === 0 ? (
            // Step 0: Choose creation source (scratch or Word document)
            <Box>
              {/* Creation option selection – only visible when DOCX feature is enabled */}
              {(enableOutlineDocx || verbatimMode) && (
                <Tabs value={outlineOption} onChange={(_, newValue) => setOutlineOption(newValue)} sx={{ mb: 2, borderBottom: '1px solid #ccc' }}>
                  <Tab
                    value="auto"
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesome fontSize="small" />
                        <Typography>{lang === 'en' ? 'Start from Scratch' : '从头开始'}</Typography>
                      </Stack>
                    }
                  />
                  {enableOutlineDocx && (
                    <Tab
                      value="docx"
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DescriptionIcon fontSize="small" />
                          <Typography>{lang === 'en' ? 'Import outline from an existing Word document' : lang === 'zh-HK' ? '從現有 Word 文件匯入大綱' : '从现有 Word 文档导入大纲'}</Typography>
                        </Stack>
                      }
                    />
                  )}
                </Tabs>
              )}

              {/* Additional form shown only when starting from scratch */}
              {outlineOption === 'auto' && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    margin="dense"
                    name="description"
                    label={lang === 'en' ? 'Project Description' : '项目描述'}
                    type="text"
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={hints.projectCreating}
                    value={formData.description}
                    onChange={handleInputChange}
                    helperText={lang === 'en' ? 'Describe your presentation requirements' : '描述您的演示文稿需求'}
                  />
                  <TextField
                    margin="dense"
                    label={lang === 'en' ? 'Presentation Duration (minutes)' : '演示时长（分钟）'}
                    type="number"
                    fullWidth
                    value={duration}
                    onChange={handleDurationChange}
                    inputProps={{ min: 1 }}
                    helperText={lang === 'en' ? 'How long will the presentation be?' : '演示将持续多长时间？'}
                  />

                  <Alert severity="warning" icon={<AutoAwesome />} sx={{ mt: 2 }}>
                    {lang === 'en'
                      ? 'Describe your presentation content and duration. AI will select a suitable template, generate a title, and recommend an appropriate number of slides.'
                      : '描述你要创建的演示内容和时长，AI 将根据你的输入，选择合适的模板，生成标题，并推荐适当幻灯片数量。'}
                  </Alert>
                </Box>
              )}

              {outlineOption === 'docx' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    {lang === 'en'
                      ? 'An outline in a Word document refers to the heading structure (Heading 1, Heading 2, etc.) that organizes your content. When importing, these headings will be used to structure your presentation slides. To create an outline in Word, use the Styles menu to apply heading formats to your section titles. You can view your document outline in Word by enabling the Navigation Pane (View tab > Navigation Pane). By default, only top level headings (Heading 1) are used as slides. You can change this in the Settings tab. Password-protected Word files are not supported.'
                      : lang === 'zh-HK'
                      ? 'Word 文件中的大綱是指組織內容的標題結構（標題 1、標題 2 等）。匯入時，這些標題將用於建立你的簡報頁面結構。要在 Word 中建立大綱，請使用「樣式」選單將標題格式套用到章節標題。你可以在 Word 中啟用導覽窗格（檢視 > 導覽窗格）來查看文件大綱。預設只會使用第一層標題（標題 1）作為投影片。你可以在設定頁籤中更改。受密碼保護的 Word 檔案暫不支援。'
                      : 'Word 文档中的大纲是指组织内容的标题结构（标题 1、标题 2 等）。导入时，这些标题将用于构建您的演示幻灯片结构。要在 Word 中创建大纲，请使用“样式”菜单将标题格式应用于您的章节标题。您可以在 Word 中启用导航窗格（视图 > 导航窗格）来查看文档大纲。默认情况下，仅使用一级标题（标题 1）作为幻灯片。您可以在设置选项卡中更改。暂不支持受密码保护的 Word 文件。'}
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 2 }} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
                        {lang === 'en'
                          ? 'Select an existing material or upload a Word document to import its outline:'
                          : lang === 'zh-HK'
                          ? '選擇一份現有材料，或上載一個 Word 文件來匯入其大綱：'
                          : '选择一个已有的材料或上传一个 Word 文档导入其大纲：'}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <MaterialPicker
                          onSelect={(materials) => handleOutlineMaterialSelect(materials[0]?._id)}
                          multiple={false}
                          title={lang === 'en' ? 'Select Existing Materials (outline only)' : '选择已上传过的材料（仅大纲）'}
                          initialSelected={selectedMaterials.map((m) => m.fileServiceFileId)}
                          accept={['docx']}
                          hasOutline
                          lang={lang}
                        >
                          {(openDialog) => (
                            <Button size="large" variant="outlined" startIcon={<ArticleIcon />} onClick={openDialog}>
                              {lang === 'en' ? 'Select Existing Materials' : '选择已上传过的材料'}
                            </Button>
                          )}
                        </MaterialPicker>

                        {/* Upload a Word document */}
                        <FileUploadDialog
                          acceptDocxOnly
                          onUpload={handleDocxUpload}
                          children={(openDialog) => (
                            <Button size="large" variant="outlined" startIcon={<UploadIcon />} onClick={openDialog}>
                              {lang === 'en' ? 'Upload a new Word Document' : lang === 'zh-HK' ? '上載新的 Word 文件' : '上传新 Word 文档'}
                            </Button>
                          )}
                          lang={lang}
                        />
                      </Stack>
                    </Grid>

                    {/* Control zone */}
                    <Grid item xs={12} md={6}>
                      {outlineMaterial && (
                        <Stack direction="column" spacing={1} alignItems="flex-start">
                          <Typography variant="overline" color="primary.main" sx={{ mb: 0 }}>
                            {lang === 'en' ? 'Outline Material' : '大纲材料'}
                          </Typography>
                          <Chip
                            icon={
                              outlineMaterial._id ? (
                                <Stack direction="row" alignItems="center" justifyContent="center" width="24px" height="24px">
                                  <MaterialStatus
                                    materialId={outlineMaterial._id}
                                    stopOnStatuses={['parsed', 'failed']}
                                    pollIntervalMs={1000}
                                    sx={{ mt: 1 }}
                                  />
                                </Stack>
                              ) : (
                                <TextSnippetIcon fontSize="small" />
                              )
                            }
                            label={outlineMaterial.fileMetadata?.filename}
                            onDelete={() => setOutlineMaterial(null)}
                            sx={{ borderRadius: 2 }}
                            style={{ marginTop: 0 }}
                          />
                        </Stack>
                      )}
                      <FormGroup>
                        {outlineMaterial && outlineMaterial.parsedOutline && outlineMaterial.parsedOutline.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <FormControl component="fieldset">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                  {lang === 'en' ? 'Select heading levels to use as slides:' : '选择用作幻灯片的标题级别：'}
                                </Typography>
                                <Chip
                                  label={(() => {
                                    const count = calculateSlidesCount();
                                    return lang === 'en' ? `${count} slide${count !== 1 ? 's' : ''}` : `${count} 页`;
                                  })()}
                                  color={calculateSlidesCount() > 0 ? 'primary' : 'default'}
                                  variant="outlined"
                                  size="small"
                                />
                              </Box>

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, alignItems: 'center' }}>
                                {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((level) => (
                                  <FormControlLabel
                                    key={level}
                                    control={
                                      <Checkbox
                                        checked={selectedHeadingLevels.includes(level)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedHeadingLevels([...selectedHeadingLevels, level]);
                                          } else {
                                            setSelectedHeadingLevels(selectedHeadingLevels.filter((l) => l !== level));
                                          }
                                        }}
                                      />
                                    }
                                    label={level.toUpperCase()}
                                    sx={{ minWidth: 'auto' }}
                                  />
                                ))}
                              </Box>
                            </FormControl>

                            <OutlinePreviewDialog
                              outline={
                                outlineMaterial?.parsedOutline
                                  ? filterOutlineByHeadingLevels(outlineMaterial.parsedOutline, selectedHeadingLevels)
                                  : undefined
                              }
                              lang={lang}
                              headingLevel="h1"
                            >
                              {(openDialog) => (
                                <Button
                                  variant="outlined"
                                  sx={{ mt: 1 }}
                                  startIcon={<Visibility />}
                                  onClick={openDialog}
                                  disabled={calculateSlidesCount() === 0}
                                >
                                  {lang === 'en' ? 'Preview Outline' : '预览纲要'}
                                </Button>
                              )}
                            </OutlinePreviewDialog>
                          </Box>
                        )}

                        {outlineMaterial &&
                          (!outlineMaterial.parsedOutline ||
                            !Array.isArray(outlineMaterial.parsedOutline) ||
                            outlineMaterial.parsedOutline.length === 0) && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {lang === 'en' ? 'No outline found in the uploaded document.' : '上传的文档中未找到纲要。'}
                            </Alert>
                          )}

                        {outlineMaterial && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(selectedMaterials.find((material) => material._id === outlineMaterial._id))}
                                onChange={handleOutlineMaterialCheckboxChange}
                              />
                            }
                            label={lang === 'en' ? 'Treat document content as reference material' : '内容作为参考材料'}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : activeStep === 1 ? (
            // Step 2: Review Recommendations
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                margin="dense"
                name="title"
                label={lang === 'en' ? 'Project Title' : '项目标题'}
                type="text"
                fullWidth
                value={formData.title}
                onChange={handleInputChange}
                required
                autoFocus={!recommendation}
              />
              <TextField
                margin="dense"
                name="description"
                label={lang === 'en' ? 'Project Description' : '项目描述'}
                type="text"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                required
              />
              <FormControl fullWidth margin="dense" required variant="standard">
                <InputLabel id="pptx-file-label">{lang === 'en' ? 'PPT Template' : 'PPT 模板'}</InputLabel>
                <Select
                  labelId="pptx-file-label"
                  name="pptxFileId"
                  value={formData.pptxFileId}
                  onChange={handleSelectChange}
                  label={lang === 'en' ? 'PPT Template' : 'PPT 模板'}
                  required
                >
                  <MenuItem value="none">
                    <em>{lang === 'en' ? 'None' : '请选择模板'}</em>
                  </MenuItem>
                  {pptxFiles.map((file) => (
                    <MenuItem key={file._id} value={file._id}>
                      {file.filename}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {recommendation?.matchReason && formData.pptxFileId === recommendation?.suggestedPptxFileId ? (
                <Alert severity="info" sx={{ mb: 1 }} icon={<AutoAwesome />}>
                  {recommendation.matchReason}
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {lang === 'en' ? 'Select a template that best matches your presentation needs' : '选择最适合您演示需求的模板'}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth margin="dense" variant="standard">
                  <InputLabel id="language-label">{lang === 'en' ? 'Presentation Language' : '演示语言'}</InputLabel>
                  <Select
                    labelId="language-label"
                    name="lang"
                    value={formData.lang || 'zh-CN'}
                    onChange={handleSelectChange}
                    label={lang === 'en' ? 'Presentation Language' : '演示语言'}
                  >
                    <MenuItem value="en">{lang === 'en' ? 'English' : '英文'}</MenuItem>
                    <MenuItem value="zh-CN">{lang === 'en' ? 'Chinese' : '简体中文'}</MenuItem>
                    <MenuItem value="zh-HK">{lang === 'en' ? 'Traditional Chinese' : '繁體中文'}</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {lang === 'en' ? 'The language used for AI-generated content and slide text' : 'AI生成内容和幻灯片文本使用的语言'}
                  </Typography>
                </FormControl>

                <TextField
                  margin="dense"
                  name="numSlides"
                  label={lang === 'en' ? 'Number of Slides' : '幻灯片数量'}
                  type="number"
                  fullWidth
                  value={formData.numSlides}
                  onChange={handleInputChange}
                  inputProps={{ min: 1 }}
                  helperText={
                    outlineOption === 'docx'
                      ? lang === 'en'
                        ? 'Number of slides based on document outline'
                        : '根据文档纲要的幻灯片数量'
                      : recommendation
                      ? lang === 'en'
                        ? 'Suggested number of slides based on duration. Only effective in Auto Outline mode.'
                        : '根据时长建议的幻灯片数量。仅在自动大纲模式下生效。'
                      : lang === 'en'
                      ? 'Estimated number of slides (can be adjusted)'
                      : '预估的幻灯片数量（可调整）'
                  }
                />
              </Box>
            </Box>
          ) : activeStep === 2 ? (
            // Step 3: Add Materials
            <Box>
              <Typography variant="h6" gutterBottom>
                {lang === 'en' ? 'Materials' : '材料'}
              </Typography>
              <Typography variant="body2" gutterBottom color="text.secondary">
                {lang === 'en'
                  ? verbatimMode
                    ? 'You can select a material to convert to PPT verbatim.'
                    : 'Add reference materials to help generate better content for your presentation. You can also upload new materials after creating the project.'
                  : verbatimMode
                  ? '可以指定某个材料一字不差地转换为 PPT。'
                  : '添加材料以供 AI 参考，为 PPT 生成更相关的内容。你也可以在创建项目后补充材料。'}
              </Typography>

              <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <MaterialPicker
                  onSelect={handleMaterialSelect}
                  multiple={true}
                  title={lang === 'en' ? 'Select Materials' : '选择材料'}
                  initialSelected={selectedMaterials.map((m) => m.fileServiceFileId)}
                  lang={lang}
                >
                  {(openDialog) => (
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={openDialog}>
                      {lang === 'en' ? 'Select Existing Materials' : '从现有材料库中选择'}
                    </Button>
                  )}
                </MaterialPicker>

                <FileUploadDialog onUpload={handleMaterialUpload} lang={lang} acceptDocxOnly>
                  {(openDialog) => (
                    <Button variant="outlined" startIcon={<UploadIcon />} onClick={openDialog}>
                      {lang === 'en' ? 'Upload New Materials' : '上传新材料'}
                    </Button>
                  )}
                </FileUploadDialog>
              </Box>

              {selectedMaterials.length > 0 && (
                <>
                  <List sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', borderRadius: 1 }}>
                    {selectedMaterials.map((material) => (
                      <ListItem
                        key={material._id}
                        secondaryAction={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {verbatimMode && (
                              <Tooltip title={lang === 'en' ? 'Use as verbatim material' : '作为逐字转换材料'}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: selectedVerbatimMaterialId === material._id ? 'primary.main' : 'divider',
                                    borderRadius: 2,
                                    px: 1,
                                    py: 0.5,
                                  }}
                                >
                                  <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
                                    {lang === 'en' ? 'Verbatim' : '逐字转换'}
                                  </Typography>
                                  <Switch
                                    size="small"
                                    checked={selectedVerbatimMaterialId === material._id}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedVerbatimMaterialId(material._id);
                                      } else if (selectedVerbatimMaterialId === material._id) {
                                        setSelectedVerbatimMaterialId('');
                                      }
                                    }}
                                  />
                                </Stack>
                              </Tooltip>
                            )}
                            <IconButton edge="end" onClick={() => handleRemoveMaterial(material._id)}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemIcon>
                          <ArticleIcon />
                        </ListItemIcon>
                        <ListItemText primary={material.fileMetadata?.filename || 'Untitled'} />
                      </ListItem>
                    ))}
                  </List>
                  {verbatimMode && selectedVerbatimMaterialId && selectedMaterials.length > 1 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {lang === 'en'
                        ? 'Since one material is set as verbatim, other materials will not be automatically referenced by AI.'
                        : '由于已将某个材料设置为逐字转换，其它材料将不会被 AI 自动参考。'}
                    </Alert>
                  )}
                </>
              )}

              {selectedMaterials.length === 0 && (
                <Box sx={{ mt: 4, mb: 2, textAlign: 'center', py: 4 }}>
                  <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {lang === 'en' ? 'No materials selected. You can add them later if needed.' : '未选择材料。如果需要，您可以稍后添加。'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : activeStep === 3 ? (
            // Step 4: Add Images
            <Box>
              <Typography variant="h6" gutterBottom>
                {lang === 'en' ? 'Images' : '图片'}
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                {lang === 'en'
                  ? 'Add images to use in your presentation slides. You can also upload new images after creating the project.'
                  : '添加要在演示文稿幻灯片中使用的图片。你也可以在创建项目后补充图片。'}
              </Typography>

              <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setIsImagePickerOpen(true)}>
                  {lang === 'en' ? 'Select Existing Photos' : '从现有图片库中选择'}
                </Button>
                <ImageUpload
                  buttonProps={{ variant: 'outlined' }}
                  onUploadSuccess={(image) => {
                    setSelectedImages([...selectedImages, image]);
                  }}
                />
              </Box>

              {selectedImages.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {selectedImages.map((image) => (
                    <Grid item xs={6} sm={4} md={3} key={image._id}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia>
                          <ProtectedImage imageUrl={image.url} alt={image.alt} />
                        </CardMedia>
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: 4, right: 4, color: 'white', bgcolor: 'rgba(0, 0, 0, 0.5)' }}
                          onClick={() => handleRemoveImage(image._id)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {selectedImages.length === 0 && (
                <Box sx={{ mt: 4, mb: 2, textAlign: 'center', py: 4 }}>
                  <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {lang === 'en' ? 'No images selected. You can add them later if needed.' : '未选择图片。如果需要，您可以稍后添加。'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}

          {/* Image Picker */}
          <ImagePickerContainer
            open={isImagePickerOpen}
            onClose={() => setIsImagePickerOpen(false)}
            onSelectImage={handleImageSelect}
            tabs={['User', 'Public']}
          />
        </DialogContent>
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2,
            flexShrink: 0,
          }}
        >
          <Button type="button" onClick={onClose} disabled={localSubmitting}>
            {lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          {activeStep === 0 ? (
            <>
              <Button type="button" onClick={() => setActiveStep((prevStep) => prevStep + 1)} disabled={localSubmitting} sx={{ ml: 1 }}>
                {lang === 'en' ? 'Skip' : '跳过'}
              </Button>

              <Button
                type="button"
                onClick={outlineOption === 'auto' ? handleGetRecommendation : handleGetDocxRecommendation}
                disabled={
                  localSubmitting ||
                  (outlineOption === 'auto'
                    ? isRecommending || !formData.description?.trim() || !duration
                    : isDocxRecommending ||
                      !outlineMaterial ||
                      !Array.isArray(outlineMaterial.parsedOutline) ||
                      outlineMaterial.parsedOutline.length === 0 ||
                      selectedHeadingLevels.length === 0)
                }
                variant="contained"
                startIcon={
                  outlineOption === 'auto' ? (
                    isRecommending ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <AutoAwesome />
                    )
                  ) : isDocxRecommending ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : undefined
                }
              >
                {isRecommending || isDocxRecommending
                  ? lang === 'en'
                    ? 'Getting Recommendations...'
                    : '正在获取建议...'
                  : lang === 'en'
                  ? 'Get AI Recommendations'
                  : '获取 AI 建议'}
              </Button>
            </>
          ) : (
            <>
              {activeStep > 0 && (
                <Button type="button" onClick={() => setActiveStep(activeStep - 1)} disabled={localSubmitting || isRecommending}>
                  {lang === 'en' ? 'Back' : '返回'}
                </Button>
              )}
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canProceed() || localSubmitting || (outlineOption === 'docx' && (!outlineMaterial || selectedHeadingLevels.length === 0))}
                variant="contained"
                endIcon={activeStep < steps.length - 1 ? <ChevronRight /> : undefined}
              >
                {activeStep === steps.length - 1 ? (lang === 'en' ? 'Create Project' : '创建项目') : lang === 'en' ? 'Next Step' : '下一步'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Slide Warning Dialog */}
      <Dialog open={showSlideWarning} onClose={() => setShowSlideWarning(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="warning" />
          {lang === 'en' ? 'Large Project Warning' : '大型项目警告'}
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {lang === 'en'
              ? 'You are about to create a presentation with more than 50 slides. This will consume significant AI resources and may result in slower performance during slide generation.'
              : '您即将创建一个包含超过 50 页幻灯片的演示文稿。这将消耗大量的 AI 资源，可能导致幻灯片生成过程变慢。'}
          </Typography>
          <Typography>{lang === 'en' ? 'Would you like to continue with this large project?' : '您是否要继续创建这个大型项目？'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSlideWarning(false)} color="primary">
            {lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          <Button
            onClick={async () => {
              setShowSlideWarning(false);
              await createProject();
            }}
            variant="contained"
            color="warning"
            disabled={localSubmitting}
            startIcon={localSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {localSubmitting ? (lang === 'en' ? 'Creating...' : '创建中...') : lang === 'en' ? 'Continue Anyway' : '仍要继续'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

export default ProjectFormDialog;
